import US_STATES from "@/utils/states.json";

// Google Places result -> street / city / state / ZIP.
//
// QA2 (Levi's mobile run): Brooklyn, Queens, the Bronx and many UK/US towns
// have no `locality` component (Brooklyn is `sublocality_level_1`). The old
// parser fell back to the state short name, so "350 5th Avenue, Brooklyn"
// was saved with the city "NY". The city now comes from the most specific
// town-level component there is, and never from the state.
// Most specific town first, neighborhood last: a Queens result carries
// locality "New York", sublocality "Queens" and neighborhood "Astoria", and
// the mail city is the locality (or, where there is none, the borough).
const CITY_TYPES = [
  "locality",
  "postal_town",
  "sublocality_level_1",
  "sublocality",
  "administrative_area_level_3",
  "neighborhood",
];

function componentOf(components, type, nameKey = "long_name") {
  const found = components.find(
    (component) => Array.isArray(component.types) && component.types.includes(type),
  );
  return found ? String(found[nameKey] || "") : "";
}

// Returns null when the place has no address parts at all (the investor
// pressed Enter on typed text instead of choosing a suggestion).
export function parseGooglePlace(place) {
  const components = place && Array.isArray(place.address_components)
    ? place.address_components
    : null;
  if (!components || !components.length) {
    return null;
  }
  const street = [
    componentOf(components, "street_number"),
    componentOf(components, "route"),
  ].filter(Boolean).join(" ");
  const city = CITY_TYPES.map((type) => componentOf(components, type)).find(Boolean) || "";
  return {
    street,
    city,
    state: componentOf(components, "administrative_area_level_1", "short_name"),
    zipcode: componentOf(components, "postal_code", "short_name"),
  };
}

// A saved "city" that is just the SAME address's own state ("NY" with state
// NY, "Ohio" with state OH) is the old Places bug's footprint. It is treated
// as missing so the investor confirms the city. Real towns that share a name
// with some other state are kept: Wyoming MI, Delaware OH, Nevada MO,
// Virginia MN. "New York" and "Washington" are real cities as well as states,
// so they are kept too. With no state to compare against nothing is wiped.
const STATE_NAME_CITIES = ["new york", "washington"];
function stateCodeOf(state) {
  const value = String(state || "").trim();
  if (!value) return "";
  if (/^[A-Za-z]{2}$/.test(value)) {
    const code = value.toUpperCase();
    return Object.prototype.hasOwnProperty.call(US_STATES, code) ? code : "";
  }
  const lowered = value.toLowerCase();
  const found = Object.keys(US_STATES).find(
    (code) => String(US_STATES[code]).toLowerCase() === lowered,
  );
  return found || "";
}
export function cityLooksLikeState(city, state) {
  const value = String(city || "").trim();
  const code = stateCodeOf(state);
  if (!value || !code) return false;
  const lowered = value.toLowerCase();
  if (lowered === code.toLowerCase()) return true;
  if (STATE_NAME_CITIES.includes(lowered)) return false;
  return lowered === String(US_STATES[code]).toLowerCase();
}

export default { parseGooglePlace, cityLooksLikeState };
